import React, { useState, useRef } from 'react';
import DrawingOptions from 'components/quote_builder_widget/DrawingOptions';
import { PostRequest } from 'components/common/index.js';

const SUCCESS_DISPLAY_DURATION_MILLISECONDS = 3000;

const QuoteBuilderForm = ({ theme, id, setId, drawing, unitOption = 'both', widgetIdentifier, widgetFormToken, onTokenRefresh }) => {
  const formRef = useRef(null);
  const [buttonState, setButtonState] = useState('ready');
  const [errorMessage, setErrorMessage] = useState(null);
  const retryCountRef = useRef(0);
  const [cartonMaterialUserPriceId, setCartonMaterialUserPriceId] = useState(null);

  const DrawingMaterialChooser = theme.DrawingMaterialChooser;
  const PrintingColorChooser = theme.PrintingColorChooser;
  const SubmitButton = theme.SubmitButton;

  const submitWithToken = (token) => {
    const catalogName = drawing?.catalog_name;
    if (!formRef.current || !catalogName) {
      return;
    }

    const formData = new FormData(formRef.current);
    const entries = Object.fromEntries(formData.entries());

    // Separate drawing options from other form fields
    // drawing.option_names is a string array from the API (e.g., ['a', 'b', 'c'])
    const drawingOptionNames = drawing?.option_names || [];
    const data = {
      drawing_options: {},
      widget_form_token: token
    };

    for (const [key, value] of Object.entries(entries)) {
      if (drawingOptionNames.includes(key)) {
        data.drawing_options[key] = value;
      } else if (!key.startsWith('__')) {
        // Skip checkbox helper fields (prefixed with __)
        data[key] = value;
      }
    }

    PostRequest({
      url: `/widget/quote-builder/${widgetIdentifier}/${catalogName}/build-quote`,
      data: data,
      callback: () => {
        retryCountRef.current = 0;
        setButtonState('success');
        setTimeout(() => {
          setButtonState('ready');
        }, SUCCESS_DISPLAY_DURATION_MILLISECONDS);
      },
      400: (responseData) => {
        retryCountRef.current = 0;
        setButtonState('ready');
        if (responseData.rules && responseData.rules.length > 0) {
          setErrorMessage(responseData.rules[0].description);
        } else if (responseData.error) {
          setErrorMessage(responseData.error);
        }
      },
      403: (responseData) => {
        // Token was invalid/expired - server returns a fresh token
        if (responseData.widget_form_token && onTokenRefresh && retryCountRef.current < 1) {
          retryCountRef.current += 1;
          onTokenRefresh(responseData.widget_form_token);
          // Retry with the new token (form values are preserved)
          submitWithToken(responseData.widget_form_token);
        } else {
          retryCountRef.current = 0;
          setButtonState('ready');
          setErrorMessage(t('quote_builder_widget.session_expired'));
        }
      },
      404: (responseData) => {
        retryCountRef.current = 0;
        setButtonState('ready');
        if (responseData.error) {
          setErrorMessage(responseData.error);
        }
      },
      500: () => {
        retryCountRef.current = 0;
        setButtonState('ready');
        setErrorMessage(t('quote_builder_widget.unexpected_error'));
      }
    }).catch(() => {
      retryCountRef.current = 0;
      setButtonState('ready');
    });
  };

  const handleSubmit = () => {
    if (buttonState !== 'ready' || !formRef.current || !drawing?.catalog_name) {
      return;
    }

    setErrorMessage(null);
    setButtonState('submitting');
    submitWithToken(widgetFormToken);
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    handleSubmit();
  };

  return (
    <form ref={formRef} onSubmit={handleFormSubmit}>
      <DrawingOptions
        theme={theme}
        id={id}
        setId={setId}
        drawing={drawing}
        unitOption={unitOption}
      />
      {widgetIdentifier && DrawingMaterialChooser && (
        <DrawingMaterialChooser widgetIdentifier={widgetIdentifier} drawingId={id} onCartonMaterialSelected={setCartonMaterialUserPriceId} />
      )}
      {widgetIdentifier && PrintingColorChooser && (
        <PrintingColorChooser widgetIdentifier={widgetIdentifier} cartonMaterialUserPriceId={cartonMaterialUserPriceId} />
      )}
      {errorMessage && (
        <div
          data-widget-element="error-message"
          style={{
            color: '#dc3545',
            padding: '8px 12px',
            marginBottom: '12px',
            fontSize: '14px'
          }}
          dangerouslySetInnerHTML={{ __html: errorMessage }}
        />
      )}
      {widgetIdentifier && SubmitButton && (
        <SubmitButton
          buttonState={buttonState}
          onClick={handleSubmit}
        />
      )}
    </form>
  );
};

export default QuoteBuilderForm;
